import React, { Fragment, useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
  Table,
  Alert,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import PageTitle from "../../Layout/AppMain/PageTitle";
import ApiService from "../../services/ApiService";

const SentFaxes = () => {
  const [faxes, setFaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [advancedSearch, setAdvancedSearch] = useState({
    fromNumber: "",
    date: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFaxes, setTotalFaxes] = useState(0);
  const [selectedFax] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchFaxes = async (
    page = 1,
    searchData = null,
    isAdvanced = false
  ) => {
    try {
      setLoading(true);
      setError("");

      let response;

      if (isAdvanced && searchData) {
        // Use advanced search endpoint
        response = await ApiService.searchSentFaxes(
          searchData.fromNumber,
          searchData.date
        );

        // For search results, we don't have pagination info
        const faxesData = response.data || response.faxes || response;
        setFaxes(Array.isArray(faxesData) ? faxesData : []);
        setTotalPages(1);
        setTotalFaxes(Array.isArray(faxesData) ? faxesData.length : 0);
        setCurrentPage(1);
      } else {
        // Use regular paginated endpoint
        response = await ApiService.getSentFaxes(page, itemsPerPage, "");

        // Debug the API response
        console.log("API Response:", response);
        console.log("itemsPerPage:", itemsPerPage);

        // Handle response structure with totalCount
        const faxesData = response.data || response.faxes || response;
        const totalCount = response.totalCount || response.total || 0;

        console.log(
          "faxesData length:",
          Array.isArray(faxesData) ? faxesData.length : 0
        );
        console.log("totalCount:", totalCount);
        console.log(
          "calculated totalPages:",
          Math.max(1, Math.ceil(totalCount / itemsPerPage))
        );

        setFaxes(Array.isArray(faxesData) ? faxesData : []);
        setTotalFaxes(totalCount);
        setTotalPages(Math.max(1, Math.ceil(totalCount / itemsPerPage)));
        setCurrentPage(page);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch sent faxes");
      console.error("Fetch faxes error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchFaxes(1, "");
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchFaxes(page);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFaxes(1, advancedSearch, true);
  };

  const handleAdvancedSearchChange = (field, value) => {
    setAdvancedSearch((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearSearch = () => {
    setAdvancedSearch({
      fromNumber: "",
      date: "",
    });
    fetchFaxes(1, "");
  };

  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
    // Recalculate totalPages immediately with the new size
    const newTotalPages = Math.max(1, Math.ceil(totalFaxes / newSize));
    setTotalPages(newTotalPages);
    // Fetch new data with the new page size
    fetchFaxes(1);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      sent: "success",
      pending: "warning",
      failed: "danger",
      delivered: "primary",
      "in-progress": "info",
    };

    return (
      <Badge color={statusMap[status?.toLowerCase()] || "secondary"}>
        {status || "Unknown"}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";

    // Basic formatting for display
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 11) {
      return `+${cleaned.charAt(0)} ${cleaned.slice(1, 4)}-${cleaned.slice(
        4,
        7
      )}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  return (
    <Fragment>
      <PageTitle
        heading="Sent Faxes"
        subheading="View and manage your sent fax history"
        icon="pe-7s-paper-plane icon-gradient bg-mean-fruit"
      />

      <Row>
        <Col lg="12">
          <Card className="main-card mb-3">
            <CardHeader className="card-header">
              <div className="card-header-title font-size-lg text-capitalize font-weight-normal">
                <i className="header-icon lnr-paper-plane icon-gradient bg-plum-plate">
                  {" "}
                </i>
                Sent Faxes History
              </div>
              <div className="btn-actions-pane-right">
                <Button
                  color="primary"
                  style={{ color: "white" }}
                  size="sm"
                  onClick={() => fetchFaxes(currentPage)}
                >
                  <i className="fa fa-refresh mr-1"></i>
                  Refresh
                </Button>
              </div>
            </CardHeader>

            <CardBody>
              {/* Search Bar */}
              <Row className="mb-3">
                <Col md="12">
                  <Card className="mb-0">
                    <CardBody className="p-3">
                      <div className="d-flex align-items-center mb-3">
                        <h6 className="mb-0 me-3">Search Faxes</h6>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={handleClearSearch}
                        >
                          Clear
                        </Button>
                      </div>

                      <form onSubmit={handleSearch}>
                        <Row>
                          <Col md="4">
                            <Input
                              type="text"
                              placeholder="From Number (e.g., +1234567890)"
                              value={advancedSearch.fromNumber}
                              onChange={(e) =>
                                handleAdvancedSearchChange(
                                  "fromNumber",
                                  e.target.value
                                )
                              }
                            />
                          </Col>
                          <Col md="4">
                            <Input
                              type="date"
                              value={advancedSearch.date}
                              onChange={(e) =>
                                handleAdvancedSearchChange(
                                  "date",
                                  e.target.value
                                )
                              }
                            />
                          </Col>
                          <Col md="4">
                            <Button
                              color="primary"
                              type="submit"
                              className="me-2"
                              style={{ color: "white" }}
                            >
                              <i className="fa fa-search"></i> Search
                            </Button>
                          </Col>
                        </Row>
                      </form>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md="9">{/* Space for future filters */}</Col>
                <Col md="3">
                  <div className="d-flex align-items-center">
                    <label className="me-2 mb-0 small">Show:</label>
                    <Input
                      type="select"
                      size="sm"
                      value={itemsPerPage}
                      onChange={(e) =>
                        handlePageSizeChange(parseInt(e.target.value))
                      }
                      style={{ width: "auto" }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </Input>
                    <span className="ms-2 small">per page</span>
                  </div>
                </Col>
                <Col md="3" className="text-right">
                  <small className="text-muted">
                    Total: {totalFaxes} faxes
                  </small>
                </Col>
              </Row>

              {error && (
                <Alert color="danger" className="mb-3">
                  <i className="fa fa-exclamation-triangle mr-2"></i>
                  {error}
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" size="lg" />
                  <div className="mt-2">Loading sent faxes...</div>
                </div>
              ) : (
                <>
                  {faxes.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">No faxes found</h5>
                      <p className="text-muted">
                        {advancedSearch.fromNumber || advancedSearch.date
                          ? "Try adjusting your search criteria."
                          : "You haven't sent any faxes yet."}
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Recipient</th>
                            <th>Status</th>
                            <th>Date Sent</th>
                            <th>Pages</th>
                            <th>Job ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {faxes.map((fax, index) => (
                            <tr key={fax.id || fax.jobId || index}>
                              <td>
                                <div>
                                  <div className="font-weight-bold">
                                    {formatPhoneNumber(fax.toNumber)}
                                  </div>
                                  {fax.userFaxNumber && (
                                    <small className="text-muted">
                                      From: {formatPhoneNumber(fax.fromNumber)}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td>{getStatusBadge(fax.status)}</td>
                              <td>
                                <small>
                                  {formatDate(fax.sentAt || fax.createdAt)}
                                </small>
                              </td>
                              <td>
                                <Badge color="light" className="badge-pill">
                                  {fax.telnyxResponse?.data?.payload
                                    ?.page_count || "N/A"}
                                </Badge>
                              </td>
                              <td>
                                <code className="text-muted">
                                  {fax.telnyxFaxId || fax.id}
                                </code>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {/* Pagination */}
                  {totalFaxes > 0 && (
                    <Row className="mt-3">
                      <Col className="d-flex justify-content-between align-items-center">
                        <div className="text-muted">
                          Showing {(currentPage - 1) * itemsPerPage + 1}-
                          {Math.min(currentPage * itemsPerPage, totalFaxes)} of{" "}
                          {totalFaxes} total faxes
                        </div>
                        <div className="d-flex align-items-center">
                          <span className="me-3 text-muted">
                            Page {currentPage} of{" "}
                            {Math.max(1, Math.ceil(totalFaxes / itemsPerPage))}
                          </span>
                          <div className="btn-group">
                            <Button
                              color="primary"
                              outline
                              size="sm"
                              disabled={currentPage <= 1}
                              onClick={() => handlePageChange(1)}
                              title="First Page"
                            >
                              <i className="fa fa-angle-double-left"></i>
                            </Button>
                            <Button
                              color="primary"
                              outline
                              size="sm"
                              disabled={currentPage <= 1}
                              onClick={() => handlePageChange(currentPage - 1)}
                              title="Previous Page"
                            >
                              <i className="fa fa-chevron-left"></i>
                            </Button>

                            {/* Page Numbers */}
                            {Math.ceil(totalFaxes / itemsPerPage) > 1 &&
                              Array.from(
                                {
                                  length: Math.min(
                                    5,
                                    Math.ceil(totalFaxes / itemsPerPage)
                                  ),
                                },
                                (_, i) => {
                                  const calculatedTotalPages = Math.ceil(
                                    totalFaxes / itemsPerPage
                                  );
                                  let pageNum;
                                  if (calculatedTotalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (
                                    currentPage >=
                                    calculatedTotalPages - 2
                                  ) {
                                    pageNum = calculatedTotalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }

                                  return (
                                    <Button
                                      key={pageNum}
                                      color="primary"
                                      outline={currentPage !== pageNum}
                                      size="sm"
                                      onClick={() => handlePageChange(pageNum)}
                                      className={
                                        currentPage === pageNum ? "active" : ""
                                      }
                                    >
                                      {pageNum}
                                    </Button>
                                  );
                                }
                              )}

                            <Button
                              color="primary"
                              outline
                              size="sm"
                              disabled={
                                currentPage >=
                                Math.ceil(totalFaxes / itemsPerPage)
                              }
                              onClick={() => handlePageChange(currentPage + 1)}
                              title="Next Page"
                            >
                              Next <i className="fa fa-chevron-right"></i>
                            </Button>
                            <Button
                              color="primary"
                              outline
                              size="sm"
                              disabled={
                                currentPage >=
                                Math.ceil(totalFaxes / itemsPerPage)
                              }
                              onClick={() =>
                                handlePageChange(
                                  Math.ceil(totalFaxes / itemsPerPage)
                                )
                              }
                              title="Last Page"
                            >
                              <i className="fa fa-angle-double-right"></i>
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Details Modal */}
      <Modal
        isOpen={detailsModal}
        toggle={() => setDetailsModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setDetailsModal(false)}>
          Fax Details
        </ModalHeader>
        <ModalBody>
          {selectedFax && (
            <Row>
              <Col md="6">
                <h6 className="text-primary">Recipient Information</h6>
                <p>
                  <strong>To Number:</strong>{" "}
                  {formatPhoneNumber(selectedFax.toNumber)}
                </p>
                <p>
                  <strong>From Number:</strong>{" "}
                  {formatPhoneNumber(selectedFax.fromNumber)}
                </p>

                <h6 className="text-primary mt-4">Transmission Details</h6>
                <p>
                  <strong>Status:</strong> {getStatusBadge(selectedFax.status)}
                </p>
                <p>
                  <strong>Telnyx Fax ID:</strong>{" "}
                  <code>{selectedFax.telnyxFaxId}</code>
                </p>
                <p>
                  <strong>Connection ID:</strong>{" "}
                  <code>{selectedFax.connectionId}</code>
                </p>
                <p>
                  <strong>Pages:</strong>{" "}
                  {selectedFax.telnyxResponse?.data?.payload?.page_count ||
                    "N/A"}
                </p>
                <p>
                  <strong>Date Sent:</strong> {formatDate(selectedFax.sentAt)}
                </p>
                <p>
                  <strong>Date Created:</strong>{" "}
                  {formatDate(selectedFax.createdAt)}
                </p>
              </Col>
              <Col md="6">
                <h6 className="text-primary">Additional Information</h6>
                <p>
                  <strong>Call Duration:</strong>{" "}
                  {selectedFax.telnyxResponse?.data?.payload
                    ?.call_duration_secs || "N/A"}{" "}
                  seconds
                </p>
                <p>
                  <strong>Direction:</strong>{" "}
                  {selectedFax.telnyxResponse?.data?.payload?.direction ||
                    "outbound"}
                </p>
                <p>
                  <strong>Media URL:</strong>
                  {selectedFax.mediaUrl ? (
                    <a
                      href={selectedFax.mediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2"
                    >
                      View Original PDF <i className="fa fa-external-link"></i>
                    </a>
                  ) : (
                    "N/A"
                  )}
                </p>

                {selectedFax.errorMessage && (
                  <>
                    <h6 className="text-danger mt-4">Error Details</h6>
                    <p className="text-danger">{selectedFax.errorMessage}</p>
                  </>
                )}

                {selectedFax.telnyxResponse?.data?.payload && (
                  <>
                    <h6 className="text-info mt-4">Telnyx Response</h6>
                    <p>
                      <strong>Event Type:</strong>{" "}
                      {selectedFax.telnyxResponse.data.event_type}
                    </p>
                    <p>
                      <strong>Occurred At:</strong>{" "}
                      {formatDate(selectedFax.telnyxResponse.data.occurred_at)}
                    </p>
                  </>
                )}
              </Col>
            </Row>
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default SentFaxes;
